<template>
  <v-layout row mb-5>
    <v-flex xs2 mr-4>
      <CostOfCareCounters
        :totalPatients="totalPatients"
        :numOfPatients="numOfPatients"
        :averageCost="averageCost"
        :isLoading="isFiltering.counters"
        :filterObject="mergeFilter({})"
        @refresh-data="loadCountersData"
        v-if="getFlag('DAS-1033')"
      />

      <HvhAnalysisVisualization class="piechart mb-4" noMinHeight v-if="getFlag('DAS-1034')">
        <template v-slot:title>
          <h2 class="hvh-h6">Months included relative to index</h2>
        </template>
        <template v-slot:visualization>
          <CohortRangeFilter class="p-4" :value="liveFilters.relativeMonths || [minMonth, maxMonth]" :min="minMonth" :max="maxMonth" @input="dateRageChanged" />
          <Loader v-if="isLoadingRelativeMonths" />
          <div class="hvh-flex aling-center px-4 pb-4">
            <CheckBox :value="liveFilters.minMax120Months" @input="(event) => minMax120MonthsSelected(event,liveFilters.relativeMonths || [minMonth, maxMonth])" />
            <span class="clickable no-wrap ml-2" @click="minMax120MonthsSelected(!liveFilters.minMax120Months,liveFilters.relativeMonths || [minMonth, maxMonth])">Continuous Enrollment</span>
          </div>
        </template>
      </HvhAnalysisVisualization>

      <HvhAnalysisSegments v-if="hasFilters" :segment="segment" title="Cohort Segments" @change="segmentsChanged" />

      <HvhAnalysisVisualization class="piechart mb-4">
        <template v-slot:title>
          <h2 class="hvh-h6">Gender Distribution</h2>
        </template>
        <template v-slot:visualization>
          <DonutchartFilter
            :data="genderDonutChartData"
            :options="pieChartAgeOptions"
            :isFiltering="isFiltering.gender"
            height="175px"
            :isChecked="isGenderChecked"
            :filter-object="mergeFilter({ demo: { sex: -1 } })"
            @chart-created="loadGenderData"
            @refresh-data="loadGenderData"
            @checked-changed="genderSelected"
          />
        </template>
      </HvhAnalysisVisualization>
      <HvhAnalysisVisualization class="piechart mb-4">
        <template v-slot:title>
          <h2 class="hvh-h6">Age Distribution</h2>
        </template>
        <template #actions>
          <label style="font-weight: bold">
            <span class="clickable" :style="showChildAgeDistribution ? underlineStyle : null" @click="showChildAgeDistribution = true">Child</span> |
            <span class="clickable" :style="!showChildAgeDistribution ? underlineStyle : null" @click="showChildAgeDistribution = false">Adult</span>
          </label>
        </template>
        <template v-slot:visualization>
          <BarchartFilter
            name-field="name"
            value-field="percent"
            :filterObject="mergeFilter({ demo: { ageFrom: 0, ageTo: 90 } })"
            :isChecked="isAgeChecked"
            :data="ageChartData"
            :options="barChartPercentOptions"
            :isFiltering="isFiltering.age"
            :displayedBarChartData="10"
            height="175px"
            @checked-changed="ageSelected"
            @chart-created="loadAgeData"
            @refresh-data="loadAgeData"
          />
        </template>
      </HvhAnalysisVisualization>
      <HvhAnalysisVisualization class="piechart mb-4">
        <template v-slot:title>
          <h2 class="hvh-h6">Insurance Distribution</h2>
        </template>
        <template v-slot:visualization>
          <BarchartFilter
            name-field="insurance"
            value-field="percent"
            :filterObject="mergeFilter({ demo: { medicalInsurance: null } })"
            :isChecked="isInsuranceChecked"
            :data="insuranceStatistics"
            :options="barChartPercentOptions"
            :isFiltering="isFiltering.insurance"
            :displayedBarChartData="10"
            height="175px"
            @checked-changed="insuranceSelected"
            @chart-created="loadInsuranceData"
            @refresh-data="loadInsuranceData"
          />
        </template>
      </HvhAnalysisVisualization>
    </v-flex>
    <v-flex grow :style="{ width: 0 }">
      <div class="v-card v-sheet theme--light mb-4">
        <div class="v-card__text">
          <h2 class="pt-2">Filters</h2>
          <LiveFiltersRibbon :segment="segment" v-model="liveFilters" :isFiltering="isFilteringAny" @rectangle-removed="rectangleRemoved" @years-removed="yearsRemoved" />
        </div>
      </div>
      <div class="v-card v-sheet theme--light mb-4">
        <div class="v-card__text">
          <v-layout>
            <div class="flex xs8 my-4 pb-5 hvh-chart">
              <MapBox
                class="mx-4"
                style="height: 100%"
                :filterObject="mergeFilter({ area: null })"
                :mapOptions="mapBoxOptions"
                :debounce="1500"
                :isFiltering="isFiltering.map"
                :selectedStates="(liveFilters.area || {}).states"
                @map-created="mapCreated"
                @refresh-data="loadMapBoxData"
                @rectangle-selected="rectangleSelected"
                @state-selected="stateSelected"
                @view-box="viewBoxChanged"
                ref="mapBox"
              />
              <div class="pt-2 hvh-flex-center">
                <label class="pr-3">{{ formatCurrency(heatMapCounts.min()) }}</label>
                <div style="width: 50%; height: 15px; background: linear-gradient(90deg, rgba(109, 232, 201, 1) 0%, rgba(39, 108, 152, 1) 100%)"></div>
                <label class="pl-3">{{ formatCurrency(heatMapCounts.max()) }}</label>
              </div>
              <div class="pt-2 hvh-flex-center">Average Cost per MSA</div>
            </div>

            <StackedHorizontalBarchartFilter
              class="flex xs4 my-4"
              name-field="year"
              value-field="cost"
              text="Average Cost PMPM"
              :filterObject="mergeFilter({ years: null })"
              :data="costBarChartData"
              :options="costBarChartOptions"
              :isChecked="isYearChecked"
              :isFiltering="isFiltering.barChart"
              :legendItems="stackedLegendItems"
              @chart-created="loadBarChartData"
              @checked-changed="yearSelected"
              @refresh-data="loadBarChartData"
              @data-created="createStackedLegend"
            />
          </v-layout>
        </div>
      </div>
      <div class="hvh-flex wrap">
        <div class="col-sm-6 col-lg-3 mb-4" v-for="(event, ind) in categoryEvents.take(4)" :key="ind">
          <HvhScrollOverlay class="hvh-card--rounded" :class="{ 'ml-4': ind % 2, 'ml-lg-4': ind > 0 && !(ind % 2) }" :title="!isFiltering[getCategoryEventDataProp(event)] && 'Use ctr + scroll to scroll'">
            <v-card class="hvh-card--rounded px-1 pt-3 pb-4">
              <CostOfCareHorizontalBarchartFilter
                :filterObject="mergeFilter({ [getCategoryEventProp(event)]: null })"
                :text="event.text"
                :data="categoryEventsData[getCategoryEventDataProp(event)] || []"
                :isChecked="isChecked(getCategoryEventProp(event))"
                :menuOptions.sync="categoryEvents[ind]"
                nameField="diagnosis"
                valueField="patientCount"
                noSortOrder
                @load-data="loadCategoryEventsBarchartDataIfNeeded(event, $event)"
                @checked-changed="chartSelected(getCategoryEventProp(event), $event)"
                @chart-created="loadCategoryEventsBarchartDataIfNeeded(event, $event)"
                @refresh-data="loadCategoryEventBarChartData(event, $event)"
                :isFiltering="isFiltering[getCategoryEventDataProp(event)]"
                :key="event.prop + ind"
              />
            </v-card>
          </HvhScrollOverlay>
        </div>
      </div>
    </v-flex>
  </v-layout>
</template>

<script>
import axios from '../../../axios';
import numeral from 'numeral';
import { customNumberFormat, Debouncer, hashCode, interpolateFnc, mergeFilters } from '../../../libraries/common';
import { stringifyLiveFilters } from '../../../libraries/liveFilters';
import { msaMapOptions } from '../../../libraries/mapBox';
import BarchartFilter from '../../analysis/filters/BarchartFilter.vue';
import DonutchartFilter from '../../analysis/filters/DonutchartFilter.vue';
import LiveFiltersRibbon from '../../analysis/filters/LiveFiltersRibbon.vue';
import MapBox from '../../analysis/filters/MapBox.vue';
import StackedHorizontalBarchartFilter from '../../analysis/filters/StackedHorizontalBarchartFilter.vue';
import HvhAnalysisSegments from '../../analysis/HvhAnalysisSegments.vue';
import HvhAnalysisVisualization from '../../analysis/HvhAnalysisVisualization.vue';
import CostOfCareHorizontalBarchartFilter from './components/CostOfCareHorizontalBarchartFilter.vue';
import HvhScrollOverlay from '../../common/overlay/HvhScrollOverlay.vue';
import CostOfCareCounters from './components/CostOfCareCounters.vue';
import CheckBox from '../../common/form/checkbox/CheckBox.vue';
import CohortRangeFilter from '../cohorts/cohortFilters/CohortRangeFilter.vue';
import { createFormData, errorDebug, } from '../../../libraries/common';
export default {
  components: { StackedHorizontalBarchartFilter, MapBox, LiveFiltersRibbon, BarchartFilter, DonutchartFilter, HvhAnalysisVisualization, HvhAnalysisSegments, HvhScrollOverlay, CostOfCareHorizontalBarchartFilter, CostOfCareCounters, CheckBox, CohortRangeFilter },
  props: {
    runId: Number,
    cohortId: Number,
    subpopulations: Object,
    totalPatients: Number,
    savedState: Object,
    isEditableByUser: Boolean,
  },
  data() {
    var liveFilters = (this.savedState && this.savedState.liveFilters) ? this.savedState.liveFilters : {
      cohortId: [this.cohortId],
      demo: {
        ageFrom: 0,
        ageTo: 90,
        sex: -1,
        medicalInsurance: null,
      },
      //relativeMonths:[-120,120]
    };
    if (liveFilters.demo.medicalInsurance && !Array.isArray(liveFilters.demo.medicalInsurance))
      liveFilters.demo.medicalInsurance = [liveFilters.demo.medicalInsurance];
    liveFilters.version = 'v2';

    var barChartOptions = {
      er: () => ({
        text: 'ER',
        levels: [
          { component: 'CostOfCareTotalCostERBarChart', prop: 'er', selected: true, },
        ]
      }),
      costIn: () => ({
        text: 'Inpatient',
        levels: [
          { component: 'CostOfCareTotalCostForInPatient', prop: 'costIn', selected: true, },
        ]
      }),
      costOut: () => ({
        text: 'Outpatient',
        levels: [
          { component: 'CostOfCareTotalCostForOutPatient', prop: 'costOut', selected: true, },
        ]
      }),
      prescription: () => ({
        text: 'Prescriptions',
        levels: [
          { component: 'CostOfCareTotalCostForPrescription', prop: 'prescription', selected: true, },
        ]
      })
    };

    var categoryEvents = Object.keys(barChartOptions)
      .filter(prop => barChartOptions[prop])
      .map(prop => {
        return {
          prop,
          text: barChartOptions[prop]().text,
          levels: barChartOptions[prop]().levels,
          // options: (barChartOptions[prop]().options || []).map(optionFnc(ind)),
          countBy: 'totalPlanPay',
        };
      });

    var allCategoryKeys = Object.keys(barChartOptions)
      //@ts-ignore
      .flatMap(key => barChartOptions[key]().levels.length ? barChartOptions[key]().levels.map(f => `${key}-${f.prop}`) : [key]);

    return {
      patientCount: this.totalPatients,
      mouseover: false,
      liveFilters,
      options: (this.savedState || {}).options,
      isFiltering: {
        ...allCategoryKeys.reduce((acc, key) => { acc[key] = false; return acc; }, {}),
        map: false,
        gender: false,
        barChart: false,
        insurance: false,
        age: false,
        patientCount: false,
        counters: true,
      },
      costStatistics: [],
      heatMapData: [],
      mapBoxOptions: {
        ...msaMapOptions,
        ...(((this.savedState || {}).options || {}).viewBox || {}),
      },
      yearsRange: liveFilters.years ? [liveFilters.years.first(), liveFilters.years.last()].filter(f => f) : [],
      genderStatistics: { maleCount: 0, femaleCount: 0 },
      ageStatistics: [],
      insuranceStatistics: [],
      pieChartAgeOptions: {
        series: [
          {
            top: 30,
          }
        ]
      },
      barChartPercentOptions: {
        tooltip: {
          formatter: (params) => {
            return params.name + '<br>' + params.marker + customNumberFormat(params.data.data.count)
          }
        },
        yAxis: {
          axisLabel: {
            formatter: '{value}%'
          },
        },
        series: [
          {
            barMaxWidth: 30
          }
        ],
      },
      ageRange: [],
      showChildAgeDistribution: false,
      underlineStyle: 'text-decoration:underline;',
      saveStateDebounceFnc: null,
      categoryEvents,
      categoryEventsData: allCategoryKeys.reduce((acc, key) => { acc[key] = null; return acc; }, {}),
      cancelRequest: {},
      debouncer: new Debouncer(2000),
      drawControl: null,
      numOfPatients: 0,
      averageCost: 0,
      // medianCost: 0,
      medianCost: 0,
      stackedLegendItems: null,
      minMonth:-120,
      maxMonth:120,
      isLoadingRelativeMonths:false
    };
  },
  methods: {
    dateRageChanged(event) {
      this.liveFilters = {
        ...this.liveFilters,
        relativeMonths: event,
      }
    },
    minMax120MonthsSelected(event,months) {
      this.liveFilters = {
        ...this.liveFilters,
        minMax120Months: event,
        relativeMonths: months,
      }
    },
    getSelectedSegmentIds(segment) {
      return [segment, ...segment.children].filter(f => f.enabled).map(f => f.cohortId);
    },
    segmentsChanged(event) {
      this.liveFilters = {
        ...this.liveFilters,
        cohortId: this.getSelectedSegmentIds(event)
      }
    },
    saveState() {
      var state = JSON.stringify({
        liveFilters: this.liveFilters,
        options: this.options
      });
      if (this.isEditableByUser) {
        if (this.cancelSaveStateRequest)
          this.cancelSaveStateRequest();
        axios.post('/Analytics/CohortAnalyticRun/' + this.runId + '/SaveState', { state }, {
          cancelToken: new axios.CancelToken(c => this.cancelSaveStateRequest = c)
        }).catch(error => {
          if (!axios.isCancel(error)) console.log(error);
        });
      } else {
        sessionStorage.setItem('savedState' + this.runId, state);
      }
    },
    rectangleSelected(event) {
      this.liveFilters = {
        ...this.liveFilters,
        area: {
          rectangle: event
        }
      };
    },
    stateSelected(event) {
      this.liveFilters = {
        ...this.liveFilters,
        area: {
          states: event
        }
      }
    },
    isChecked(prop) {
      return item => (this.liveFilters[prop] || []).includes(item.id);
    },
    loadCategoryEventsBarchartDataIfNeeded(event, filterObject) {
      if (!this.categoryEvents.filter(f => f !== event).map(f => this.getCategoryEventDataProp(f)).includes(this.getCategoryEventDataProp(event))
        || (!this.categoryEventsData[this.getCategoryEventDataProp(event)] && !this.isFiltering[this.getCategoryEventDataProp(event)]))
        this.loadCategoryEventBarChartData(event, filterObject);
    },
    getCategoryEventDataProp(event) {
      var detailOption = (event.levels || []).find(f => f.selected);
      var option = (event.options || []).find(f => f.selected);
      if (option) return `${event.prop}-${option.text}`;
      return detailOption ? `${event.prop}-${detailOption.prop}` : event.prop;
    },
    getCategoryEventProp(event) {
      return ((event.levels || []).find(f => f.selected) || {}).prop || event.prop;
    },
    chartSelected(prop, event) {
      var isInCollection = (this.liveFilters[prop] || []).includes(event.id);
      this.liveFilters = {
        ...this.liveFilters,
        [prop]: (isInCollection ? this.liveFilters[prop].filter(f => f !== event.id) : [...(this.liveFilters[prop] || []), event.id]),
      };
    },
    loadCategoryEventBarChartData(event, filterObject) {
      var dataProp = this.getCategoryEventDataProp(event);
      if (this.cancelRequest[dataProp])
        this.cancelRequest[dataProp]();
      this.$set(this.isFiltering, dataProp, true);
      var detailOption = (event.levels || []).find(f => f.selected);
      var option = ((event.options || []).find(f => f.selected) || {}).text;

      axios.post('/Analytics/CohortAnalyticRun/LiveResult', {
        runId: this.runId,
        component: detailOption ? detailOption.component : ('CostOfCare' + event.prop.capitalize()),
        option,
        ...this.stringifyLiveFilters(filterObject),
      }, {
        cancelToken: new axios.CancelToken(c => this.cancelRequest[dataProp] = c)
      }).then((response) => {
        this.categoryEventsData[dataProp] = response.data
          .map(f => ({
            averagePlanPay: f.totalPlanPay / f.patientCount,
            ...f,
          }));
        this.$set(this.isFiltering, dataProp, false);
      }).catch((error) => {
        if (!axios.isCancel(error)) {
          console.log(error);
          this.$set(this.isFiltering, dataProp, false);
          this.disableIfTimeout(error);
        }
      });
    },
    disableIfTimeout(error) {
      if (error.response.status === 504) {
        this.$alert('Error', 'Loading of data failed. Please reload page to try loading data again.');
      }
    },
    stringifyLiveFilters(liveFilters) {
      return stringifyLiveFilters(liveFilters);
    },
    loadCountersData() {
      if (this.cancelRequest.counters)
        this.cancelRequest.counters();

      this.isFiltering.counters = true;
      axios.post('/Analytics/CohortAnalyticRun/LiveResult', {
        runId: this.runId,
        component: 'CostOfCareStats',
        ...this.stringifyLiveFilters(this.liveFilters),
      }, {
        cancelToken: new axios.CancelToken(c => this.cancelRequest.counters = c)
      }).then(({ data }) => {
        this.numOfPatients = data.numOfPatients;
        this.totalCost = data.totalCost;
        this.averageCost = data.averageCost;
        this.isFiltering.counters = false;
      }).catch((error) => {
        if (!axios.isCancel(error)) {
          console.log(error);
          this.isFiltering.counters = false;
          this.disableIfTimeout(error);
        }
      });
    },
    yearSelected(event) {
      if (event.year > 2000 && event.year < 3000) {
        if (this.yearsRange.length && this.yearsRange.filter(f => f === event.year).length === this.yearsRange.length) {
          this.yearsRange = [];
        } else {
          if (this.yearsRange.length > 1)
            this.yearsRange.shift();
          this.yearsRange.push(event.year);
        }

        this.liveFilters = {
          ...this.liveFilters,
          years: this.yearsRange.length ? parseInt(this.yearsRange.min()).rangeTo(parseInt(this.yearsRange.max()) + 1) : null,
        };
      }
    },
    isYearChecked(event) {
      return event.year >= this.yearsRange.min() && event.year <= this.yearsRange.max();
    },
    genderSelected(event) {
      var sex = this.liveFilters.demo.sex === -1 ? event.filterValue : -1;
      this.liveFilters = {
        ...this.liveFilters,
        demo: {
          ...this.liveFilters.demo,
          sex,
        }
      };
    },
    isGenderChecked(event) {
      return this.liveFilters.demo.sex === -1 || this.liveFilters.demo.sex === event.filterValue;
    },
    insuranceSelected(event) {
      var medicalInsuranceUpdated = this.liveFilters.demo.medicalInsurance?.includes(event.insurance) ? [] : [event.insurance];
      this.liveFilters = {
        ...this.liveFilters,
        demo: {
          ...this.liveFilters.demo,
          medicalInsurance:this.liveFilters.demo.medicalInsurance
      ? [...this.liveFilters.demo.medicalInsurance, ...medicalInsuranceUpdated]
      : [...medicalInsuranceUpdated]
        }
      };
    },
    isInsuranceChecked(event) {
      return !(this.liveFilters.demo.medicalInsurance || []).any() || this.liveFilters.demo.medicalInsurance.includes(event.insurance);
    },
    ageSelected(event) {
      if (this.ageRange.length && this.ageRange.filter(f => f.minAge === event.minAge && f.maxAge === event.maxAge).length === this.ageRange.length) {
        this.ageRange = [];
      } else {
        if (this.ageRange.length > 1)
          this.ageRange.shift();
        this.ageRange.push(event);
      }

      this.liveFilters = {
        ...this.liveFilters,
        demo: {
          ...this.liveFilters.demo,
          ageFrom: this.ageRange.length ? this.ageRange.min(f => f.minAge) : 0,
          ageTo: this.ageRange.length ? this.ageRange.max(f => f.maxAge) : 90
        }
      };
    },
    isAgeChecked(event) {
      return event.minAge >= this.liveFilters.demo.ageFrom && event.maxAge <= this.liveFilters.demo.ageTo;
    },
    mergeFilter(obj) {
      return mergeFilters(this.liveFilters, obj);
    },
    loadBarChartData(filterObject) {
      if (this.cancelBarChartDataRequest)
        this.cancelBarChartDataRequest();
      this.isFiltering.barChart = true;
      axios.post('/Analytics/CohortAnalyticRun/LiveResult', {
        runId: this.runId,
        component: "CostOfCareBarChart",
        ...filterObject,
      }, {
        cancelToken: new axios.CancelToken(c => this.cancelBarChartDataRequest = c)
      }).then(response => {
        this.costStatistics = response.data;
        this.isFiltering.barChart = false;
      }).catch(error => {
        if (!axios.isCancel(error)) {
          console.log(error);
          this.isFiltering.barChart = false;
        }
      });
    },

    mapCreated(map, mapboxgl, drawControl) {
      this.map = map;
      // Create a popup, but don't add it to the map yet.
      var popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false
      });

      map.on('mouseenter', 'point', (e) => {
        map.getCanvas().style.cursor = 'pointer';

        var coordinates = e.features[0].geometry.coordinates.slice();
        var description = `${e.features[0].properties.name}
                <br />Average cost: ${this.formatCurrency(e.features[0].properties.avgcost)}
                <br />Patient Count: ${e.features[0].properties.patientCount}`;
        popup.setLngLat(coordinates).setHTML(description).addTo(map);
      });

      map.on('mouseleave', 'point', function () {
        map.getCanvas().style.cursor = '';
        popup.remove();
      });

      map.on('zoom', () => map.getSource('points').setData(this.createPointSource(map)));

      if (this.liveFilters.area) {
        if (this.liveFilters.area.circle)
          drawControl.addCircle(this.liveFilters.area.circle.center, this.liveFilters.area.circle.r);
        if (this.liveFilters.area.rectangle)
          drawControl.addRectangle(this.liveFilters.area.rectangle.swLngLat, this.liveFilters.area.rectangle.neLngLat);
      }

      this.loadMapBoxData(map, this.mergeFilter({ area: null }));

      this.drawControl = drawControl;
    },
    createPointSource(map) {
      var interpolateSize = interpolateFnc(this.heatMapCounts, [{ value: [1], percent: 0 }, { value: [20], percent: 100 }]);

      return {
        'type': 'FeatureCollection',
        'features': this.heatMapData
          .map(f => ({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: f.lngLat,
            },
            'properties': {
              avgcost: f.avgcost,
              patientCount: f.patientCount,
              size: interpolateSize(f.avgcost)[0] * map.getZoom() / 3 + 4,
              name: f.name,
              color: `rgb(${this.gradientColorFnc(Math.log(Math.max(f.avgcost, 1))).join(',')})`,
            }
          })),
      };
    },
    loadMapBoxData(map, filterObject) {
      if (this.cancelMapBoxDataRequest)
        this.cancelMapBoxDataRequest();
      this.isFiltering.map = true;
      axios.post('/Analytics/CohortAnalyticRun/LiveResult', {
        runId: this.runId,
        component: "CostOfCareMap",
        ...filterObject,
      }, {
        cancelToken: new axios.CancelToken(c => this.cancelMapBoxDataRequest = c)
      }).then(({ data }) => {
        this.heatMapData = data;
        var pointsSource = this.createPointSource(map);
        if (!map.getSource('points')) {
          map.addSource('points', {
            'type': 'geojson',
            'data': pointsSource
          });

          map.addLayer({
            id: 'point',
            // 'source': 'point',
            type: 'circle',
            source: 'points',
            paint: {
              'circle-radius': ['get', 'size'],
              'circle-color': ['get', 'color'],
              'circle-opacity': 0.8,
            }
          });
        } else {
          map.getSource('points').setData(this.createPointSource(map));
        }
        this.isFiltering.map = false;
      }).catch(function (error) {
        if (!axios.isCancel(error)) {
          console.log(error);
          this.isFiltering.map = false;
        }
      });
    },
    viewBoxChanged(event) {
      this.options = {
        ...(this.options || {}),
        viewBox: event,
      };
    },
    loadGenderData(filterObject) {
      if (this.cancelGenderDataRequest)
        this.cancelGenderDataRequest();
      this.isFiltering.gender = true;
      axios.post('/Analytics/CohortAnalyticRun/LiveResult', {
        runId: this.runId,
        component: "CostOfCareGenderDistribution",
        ...filterObject,
      }, {
        cancelToken: new axios.CancelToken(c => this.cancelGenderDataRequest = c)
      }).then(response => {
        this.genderStatistics = response.data;
        this.isFiltering.gender = false;
      }).catch(error => {
        if (!axios.isCancel(error)) {
          console.log(error);
          this.isFiltering.gender = false;
        }
      });
    },
    loadAgeData(filterObject) {
      if (this.cancelAgeDataRequest)
        this.cancelAgeDataRequest();
      this.isFiltering.age = true;
      axios.post('/Analytics/CohortAnalyticRun/LiveResult', {
        runId: this.runId,
        component: "CostOfCareAgeDistribution",
        ...filterObject,
      }, {
        cancelToken: new axios.CancelToken(c => this.cancelAgeDataRequest = c)
      }).then(response => {
        this.ageStatistics = response.data;
        this.isFiltering.age = false;
      }).catch(error => {
        if (!axios.isCancel(error)) {
          console.log(error);
          this.isFiltering.age = false;
        }
      });
    },
    loadInsuranceData(filterObject) {
      if (this.cancelInsuranceDataRequest)
        this.cancelInsuranceDataRequest();
      this.isFiltering.insurance = true;
      axios.post('/Analytics/CohortAnalyticRun/LiveResult', {
        runId: this.runId,
        component: "CostOfCareInsuranceDistribution",
        ...filterObject,
      }, {
        cancelToken: new axios.CancelToken(c => this.cancelInsuranceDataRequest = c)
      }).then((response) => {
        var sum = response.data.sum(f => f.count);
        this.insuranceStatistics = response.data
          .map(f => ({
            ...f,
            percent: f.count / sum * 100,
          }));
        this.isFiltering.insurance = false;
      }).catch(error => {
        if (!axios.isCancel(error)) {
          console.log(error);
          this.isFiltering.insurance = false;
        }
      });
    },
    formatCurrency(value) {
      return numeral(value).format('$0.00 a');
    },
    itemUncheckedName(item) {
      return `${item.category}-${item.groupby}`;
    },
    yearsRemoved() {
      this.yearsRange = [];
    },
    rectangleRemoved() {
      this.$refs.mapBox.removeDrawings();
    },
    createStackedLegend(event) {
      var translation = {
        inpat: 'Inpatient',
        outpat: 'Outpatient',
        rx: 'RX',
        //  ED
      }
      this.stackedLegendItems = event.series
        .map(f => f.data.first())
        .map(f => ({
          text: translation[f.data.name] || f.data.name,
          color: f.defaultColor,
          path: 'M0 0h12v12H0V0z',
        }));
    },
    customNumberFormat,

    getMonthsRelativeToIndex(){
      this.isLoadingRelativeMonths = true;
      axios.post('/Analytics/CohortAnalyticRun/GetRelativeMonthsToIndex', createFormData({ runId: this.runId, cohortId: this.cohortId }))
        .then(({ data }) => {
          this.minMonth = data[0];
          this.maxMonth = data[1];
        })
        .catch(errorDebug)
        .then(() => this.isLoadingRelativeMonths = false);
    }
  },
  computed: {
    segment() {
      return {
        name: "View full Cohort",
        enabled: this.liveFilters.cohortId.includes(this.cohortId),
        cohortId: this.cohortId,
        children: Object.keys(this.subpopulations)
          .map(key => parseInt(key))
          .map(filterId => ({
            name: this.subpopulations[filterId],
            enabled: this.liveFilters.cohortId.includes(filterId),
            cohortId: filterId
          }))
      };
    },
    livePatientCount() {
      let s = this.liveFilters.demo.sex;
      let c = 0;
      if (s == 2)
        c = this.genderStatistics.femaleCount;
      if (s == 1)
        c = this.genderStatistics.maleCount
      if (s == -1)
        c = this.genderStatistics.maleCount + this.genderStatistics.femaleCount
      return c;
    },
    hasFilters() { return this.segment.children.length; },
    heatMapCounts() {
      return this.heatMapData.map(f => f.avgcost);
    },
    gradientColorFnc() {
      return interpolateFnc(this.heatMapCounts.map(f => Math.log(Math.max(f, 1))), [{ value: [96, 232, 197], percent: 0 }, { value: [39, 108, 152], percent: 100 }]);
    },
    costBarChartData() {
      var years = this.costStatistics.flatMap(g => g).groupBy(f => f.year).map(f => f.key);
      var yearsReversed = years.sortBy(f => {
        var spl = f.split(' - ').map(f => parseInt(f));
        return spl[0] < spl[1] ? spl[0] : -spl[0];
      });
      var grouped = this.costStatistics.groupBy(f => f.name);
      return grouped.sortBy(f => f.key)
        .map(f => yearsReversed.map(year => f.items.find(g => g.year == year) || { ...f.items[0], year, cost: 0 }));
    },
    costBarChartOptions() {
      return {
        xAxis:
        {
          axisLabel: {
            formatter: this.formatCurrency,
            rotate: 30,
          }
        },
        tooltip: {
          formatter: (params) => {
            const marker = `<dt style="width:10px;height:10px;display:inline-block;margin-right:5px;">
                            <svg viewBox="0 0 12 12"><path fill="${params.data.defaultColor}" d="M0 0h12v12H0z"></path></svg>
                        </dt>`;
            return params.data.data.name + '<br>' + marker + this.formatCurrency(params.data.value)
          },
        },
        grid: {
          left: '30px',
        },
      };
    },
    genderDonutChartData() {
      let maleCount = this.genderStatistics.maleCount;
      let femaleCount = this.genderStatistics.femaleCount;

      return [
        {
          name: 'Male',
          value: maleCount,
          formattedValue: customNumberFormat(maleCount),
          filterValue: 1,
        },
        {
          name: 'Female',
          value: femaleCount,
          formattedValue: customNumberFormat(femaleCount),
          filterValue: 2
        }
      ]
    },
    ageChartData() {
      var sum = this.ageStatistics.sum(f => f.count);
      var groupFnc = this.showChildAgeDistribution ?
        f => (f.minAge < 18 ? `${f.minAge}-${f.maxAge}` : null)
        : f => (f.minAge < 18 ? '0-17' : `${f.minAge}${f.maxAge > 65 ? '+' : '-' + f.maxAge}`);

      var grouped = this.ageStatistics.filter(groupFnc).sortBy(f => f.minAge).groupBy(groupFnc);
      return grouped
        .map(f => {
          var count = f.items.sum(f => f.count);
          return {
            minAge: f.items.min(f => f.minAge),
            maxAge: Math.min(f.items.max(f => f.maxAge), 90),
            count,
            name: f.key,
            percent: count / sum * 100,
          };
        });
    },
    filterHash() {
      return hashCode([this.liveFilters || {}, this.options || {}]);
    },
    isFilteringAny() {
      return Object.keys(this.isFiltering).find(key => this.isFiltering[key]);
    },
  },
  watch: {
    filterHash() {
      this.debouncer.clear().push(this.saveState);
    },
  },
  mounted() {
    if (process.env.NODE_ENV === 'development') window['thisCostOfCare'] = this;
    this.getMonthsRelativeToIndex();

  }
}
</script>
